import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Container,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ko } from 'date-fns/locale';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatErrorMessage } from '../utils';
import dayjs from 'dayjs';
import { format } from 'date-fns';

interface SaleItem {
  id: number;
  item: string;
  amount: number;
  state: string;
  release_date: string | null;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
}

interface Sale {
  id: number;
  company: {
    id: number;
    name: string;
  };
  site: {
    id: number;
    name: string;
  };
  items: SaleItem[];
  created_at: string;
}

interface ProductionItem {
  id: number;
  item: string;
  quantity: number;
  unit: string;
  status: string;
  created_at: string;
}

interface ProductionForm {
  item: string;
  quantity: string;
  unit: string;
  status: string;
}

const stateOptions = [
  { value: '대기', label: '대기' },
  { value: '생산', label: '생산' },
  { value: '생산완료', label: '생산완료' },
  { value: '출고', label: '출고' },
  { value: '계산서발행', label: '계산서발행' },
];

const ProductionManagement: React.FC = () => {
  const { token } = useAuth();
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<SaleItem | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [productions, setProductions] = useState<ProductionItem[]>([]);
  const [formData, setFormData] = useState<ProductionForm>({
    item: '',
    quantity: '',
    unit: '',
    status: '진행중',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/sales');
      console.log("📦 받아온 데이터", response.data);
      setSales(response.data);
    } catch (error) {
      console.error('매출 목록 조회 오류:', error);
      enqueueSnackbar('매출 목록을 불러오는데 실패했습니다.', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchProductions();
  }, []);

  const fetchProductions = async () => {
    try {
      const response = await api.get('/api/productions');
      setProductions(response.data);
    } catch (error) {
      console.error('생산 목록 조회 중 오류:', error);
      enqueueSnackbar(formatErrorMessage(error), { variant: 'error' });
    }
  };

  const handleDateChange = async (item: SaleItem, field: 'start_date' | 'end_date' | 'release_date', date: Date | null) => {
    if (!date) return;

    try {
      const formattedDate = format(date, 'yyyy-MM-dd');
      
      // DB 업데이트
      await api.put(`/api/sale-items/${item.id}`, {
        [field]: formattedDate
      });
      
      // 서버에서 최신 데이터 가져오기
      const response = await api.get('/api/sales');
      setSales(response.data);
      
      enqueueSnackbar('생산 일정이 수정되었습니다.', { variant: 'success' });
    } catch (error) {
      console.error('생산 일정 수정 중 오류 발생:', error);
      enqueueSnackbar('생산 일정 수정에 실패했습니다.', { variant: 'error' });
    }
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case '대기':
        return 'default';
      case '생산':
        return 'warning';
      case '생산완료':
        return 'info';
      case '출고':
        return 'primary';
      default:
        return 'default';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const numericData = {
        ...formData,
        quantity: Number(formData.quantity),
      };
      await api.post('/api/productions', numericData);
      enqueueSnackbar('생산이 성공적으로 등록되었습니다.', { variant: 'success' });
      setFormData({
        item: '',
        quantity: '',
        unit: '',
        status: '진행중',
      });
      fetchProductions();
    } catch (error) {
      console.error('생산 등록 중 오류:', error);
      enqueueSnackbar(formatErrorMessage(error), { variant: 'error' });
    }
  };

  const handleEdit = async (production: ProductionItem) => {
    try {
      const updatedData = {
        ...production,
        quantity: Number(production.quantity),
      };
      await api.put(`/api/productions/${production.id}`, updatedData);
      enqueueSnackbar('생산이 성공적으로 수정되었습니다.', { variant: 'success' });
      fetchProductions();
    } catch (error) {
      console.error('생산 수정 중 오류:', error);
      enqueueSnackbar(formatErrorMessage(error), { variant: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/api/productions/${id}`);
      enqueueSnackbar('생산이 성공적으로 삭제되었습니다.', { variant: 'success' });
      fetchProductions();
    } catch (error) {
      console.error('생산 삭제 중 오류:', error);
      enqueueSnackbar(formatErrorMessage(error), { variant: 'error' });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            생산관리
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>업체명</TableCell>
                    <TableCell>현장</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(sales) && sales.length > 0 ? (
                    sales.map((sale) => (
                      <React.Fragment key={sale.id}>
                        <TableRow>
                          <TableCell>{sale.company.name}</TableCell>
                          <TableCell>{sale.site.name}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell width="30%">생산 항목</TableCell>
                                  <TableCell width="15%" align="center">상태</TableCell>
                                  <TableCell width="15%" align="center">시작일</TableCell>
                                  <TableCell width="15%" align="center">종료일</TableCell>
                                  <TableCell width="15%" align="center">출고일</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Array.isArray(sale.items) && sale.items.length > 0 ? (
                                  sale.items.map((item) => (
                                    <TableRow key={item.id} sx={{ backgroundColor: '#f5f5f5' }}>
                                      <TableCell>{item.item}</TableCell>
                                      <TableCell align="center">
                                        <Chip
                                          label={item.state || '대기'}
                                          color={getStateColor(item.state || '대기')}
                                          size="small"
                                        />
                                      </TableCell>
                                      <TableCell align="center">
                                        <DatePicker
                                          value={item.start_date ? new Date(item.start_date) : null}
                                          onChange={(date) => handleDateChange(item, 'start_date', date)}
                                          slotProps={{
                                            textField: {
                                              size: "small",
                                              sx: { width: '150px' },
                                              InputProps: {
                                                readOnly: true,
                                                sx: { fontSize: '0.875rem' }
                                              },
                                            },
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell align="center">
                                        <DatePicker
                                          value={item.end_date ? new Date(item.end_date) : null}
                                          onChange={(date) => handleDateChange(item, 'end_date', date)}
                                          slotProps={{
                                            textField: {
                                              size: "small",
                                              sx: { width: '150px' },
                                              InputProps: {
                                                readOnly: true,
                                                sx: { fontSize: '0.875rem' }
                                              },
                                            },
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell align="center">
                                        {item.release_date ? new Date(item.release_date).toLocaleDateString() : '-'}
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  <TableRow>
                                    <TableCell colSpan={5} align="center">
                                      등록된 생산 항목이 없습니다.
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        등록된 판매 내역이 없습니다.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default ProductionManagement; 